/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Card, Col, Form, Tabs, TabsProps } from "antd";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { RcFile } from "antd/es/upload";
import { useEffect, useState } from "react";

import { useAppDispatch } from "../../../../../config/hooks";
import GeneralTab from "./Tabs/general";
import LaborTab from "./Tabs/labor";
import { updateService, uploadUserImage } from "../../ducks/services";
import SuccessMessage from "../../../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import ErrorMessage from "../../../../../atoms/Toasts/ErrorMessage";
import { ServiceLineListingType } from "../../ducks/types";
import { getServiceFormData } from "../../ducks/actions";

interface FormValues {
  name: string;
  description: string;
  frequency: string;
  quantity_unit: string;
  take_off: string;
  subcontractors: string;
  category_ids: string[];
  production_rate: number;
  materials: { manager: string; use_rate: number; unit_order: string }[];
  equipment: string[];
}

interface PropsType {
  setCurrent?: (current: number) => void;
  setLoading: (loading: boolean) => void;
  companyId: string;
  serviceLineId: string;
  type: null | string;
  parentId?: string;
  serviceId?: string;
  serviceDetailData: ServiceLineListingType | null;
}

interface materialDropDownType {
  label: string;
  options: { value: string; label: string; color?: string; type: string }[];
}

export default function AddServiceStep2(props: PropsType) {
  const {
    setCurrent,
    setLoading,
    companyId,
    serviceLineId,
    type,
    parentId,
    serviceId,
    serviceDetailData,
  } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [serviceType, setServiceType] = useState("in_house");
  const [frequencyType, setFrequencyType] = useState("one_time");
  const [takeOff, isTakeOff] = useState(false);
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [addEquipment, setAddEquipment] = useState(false);
  const [takeOffType, setTakeOffType] = useState("...");
  const [unitHour, setUnitHour] = useState(true);
  const [combineDropDown, setCombineDropDown] = useState<
    materialDropDownType[]
  >([]);
  const [activeTab, setActiveTab] = useState("1");
  const [tickIcon, showTickIcon] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = (val: FormValues) => {
    if (activeTab === "1") {
      const nameValue = val?.name;
      const unitValue = val?.quantity_unit;
      const takeOffValue = val?.take_off;

      if (
        type === "simple" &&
        nameValue?.length > 0 &&
        (unitValue?.length > 0 || takeOffValue?.length > 0)
      ) {
        showTickIcon(true);
        setActiveTab("2");
      } else if (type !== "simple" && nameValue?.length > 0) {
        showTickIcon(true);
        setActiveTab("2");
      } else {
        showTickIcon(false);
      }
      return;
    }
    setLoading(true);

    const dummyArray: any[] = [];

    if (val?.materials?.length > 0) {
      val?.materials?.map((e: { manager: string }) => {
        combineDropDown?.forEach((resp) => {
          const filteredValue = resp?.options?.find(
            (opt) => opt?.value === e?.manager
          );
          if (filteredValue) {
            dummyArray.push(filteredValue);
          }
          return filteredValue;
        });

        return true;
      });
    }
    const materialsPayload = dummyArray?.map((item) => {
      // Create the base object for the payload item
      const payloadItem = {
        material_type: item.type, // Use the 'type' property from your input item
        use_rate:
          val?.materials?.find((e) => e?.manager === item?.value)?.use_rate ??
          0, // Get use_rate from the input item
        unit_order:
          val?.materials?.find((e) => e?.manager === item?.value)?.unit_order ??
          "material_unit_per_measurement_unit", // Get unit_order from the input item
        material_category_id: undefined,
        material_id: undefined,
      };

      // Add the appropriate ID based on the item's type
      if (item.type === "category") {
        payloadItem.material_category_id = item.value; // Use the item's value as the category ID
      } else if (item.type === "material") {
        payloadItem.material_id = item.value; // Use the item's value as the material ID
      }

      // Return the transformed object for this item
      return payloadItem;
    });

    const payload = {
      name: val?.name,
      service_line_id: serviceLineId,
      company_id: companyId,
      parent_id: parentId ?? undefined,
      type: type,
      description: val?.description,
      service_provision_type: serviceType,
      frequency:
        frequencyType === "one_time"
          ? "one_time"
          : type === "subservice"
          ? "recurring"
          : val?.frequency,
      takeoff_id: takeOff ? val?.take_off : "",
      unit_id: takeOff ? "" : val?.quantity_unit,
      production_rate:
        serviceType === "in_house" ? val?.production_rate : undefined,
      production_rate_type: "unit_per_hour",
      category_ids:
        val?.category_ids?.length > 0 ? val?.category_ids : undefined,
      materials: serviceType === "in_house" ? materialsPayload : undefined,
      equipments: serviceType === "in_house" ? val?.equipment : undefined,
    };

    updateService(serviceId as string, payload)
      .then(() => {
        if (checklistFile && serviceId) {
          uploadUserImage({
            owner_id: serviceId,
            image: checklistFile as File | RcFile,
            image_type: "service_image",
          })
            .then(() => {
              setLoading(false);
              navigate(
                `/company-settings/service-lines/${serviceLineId}/${companyId}`
              );
              SuccessMessage("Service updated");
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          navigate(
            `/company-settings/service-lines/${serviceLineId}/${companyId}`
          );
          SuccessMessage("Service updated");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const onChange = (key: string) => {
    setActiveTab(key);
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "General details",
      children: (
        <GeneralTab
          control={control}
          errors={errors}
          setLoading={setLoading}
          serviceType={serviceType}
          setValue={setValue}
          frequencyType={frequencyType}
          takeOff={takeOff}
          type={type}
          isTakeOff={isTakeOff}
          setServiceType={setServiceType}
          setFrequencyType={setFrequencyType}
          checklistFile={checklistFile}
          setChecklistFile={setChecklistFile}
          serviceId={serviceId}
          serviceDetailData={serviceDetailData}
          setTakeOffType={setTakeOffType}
        />
      ),
    },
    {
      key: "2",
      label:
        type === "simple"
          ? serviceType === "in_house"
            ? "Labor, Materials, Equipment"
            : "Subcontractors"
          : "Subservice categories",
      children: (
        <LaborTab
          control={control}
          errors={errors}
          watch={watch}
          type={type}
          setLoading={setLoading}
          serviceType={serviceType}
          companyId={companyId}
          combineDropDown={combineDropDown}
          addEquipment={addEquipment}
          setCombineDropDown={setCombineDropDown}
          setAddEquipment={setAddEquipment}
          takeOffType={takeOffType}
          unitHour={unitHour}
          setUnitHour={setUnitHour}
          setValue={setValue}
        />
      ),
    },
  ];

  useEffect(() => {
    if (companyId) {
      dispatch(getServiceFormData(companyId));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    if (serviceDetailData) {
      setValue("name", serviceDetailData?.name);
      setValue("description", serviceDetailData?.description);
      setValue("production_rate", serviceDetailData?.production_rate);

      setServiceType(serviceDetailData?.service_provision_type);
      setFrequencyType(
        serviceDetailData?.frequency === "one_time" ? "one_time" : "recurring"
      );

      if (serviceDetailData?.frequency !== "one_time") {
        setValue("frequency", serviceDetailData?.frequency);
      }

      if (serviceDetailData?.unit_id) {
        setValue("quantity_unit", serviceDetailData?.unit_id);
      } else {
        setValue("quantity_unit", "");
      }

      if (serviceDetailData?.takeoff_id) {
        setValue("take_off", serviceDetailData?.takeoff_id);
        isTakeOff(true);
      } else {
        setValue("take_off", "");
        isTakeOff(false);
      }

      if (serviceDetailData?.serviceEquipments?.length > 0) {
        setAddEquipment(true);
        setValue(
          "equipment",
          serviceDetailData.serviceEquipments.map(
            (item: { equipment_id: string }) => item?.equipment_id
          )
        );
      } else {
        setAddEquipment(false);
        setValue("equipment", []);
      }

      if (serviceDetailData?.serviceMaterials?.length > 0) {
        const materialData: {
          manager: string;
          use_rate: number;
          unit_order: string;
        }[] = [];
        serviceDetailData.serviceMaterials?.map(
          (e: {
            material_category_id: string;
            material_id: string;
            unit_order: string;
            use_rate: number;
          }) => {
            return materialData.push({
              manager: e?.material_category_id || e?.material_id,
              use_rate: e?.use_rate,
              unit_order: e?.unit_order,
            });
          }
        );
        setValue("materials", materialData);
      } else {
        setAddEquipment(false);
        setValue("materials", []);
      }
    }
  }, [serviceDetailData, setValue]);

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Col style={{ width: "672px", paddingBottom: 85 }}>
        <div className="title">
          <h2 style={{ marginBottom: "4px" }}>
            Edit {parentId ? "subservice" : "service"}{" "}
          </h2>
          <p style={{ fontSize: "16px" }}>
            A service becomes a work order, which your team schedules and
            completes.
          </p>
        </div>

        <div className="service_tabs">
          <Tabs
            activeKey={activeTab}
            items={items}
            onChange={onChange}
            className={tickIcon ? "tabsParent showTickIcon" : "tabsParent"}
          />
        </div>
      </Col>
      <div className="fixed_btm_card">
        <Card>
          <div className="centered">
            <Button
              htmlType="button"
              className="outlined-btn"
              onClick={() =>
                parentId || serviceId
                  ? navigate(
                      `/company-settings/service-lines/${serviceLineId}/${companyId}`
                    )
                  : setCurrent && setCurrent(0)
              }
            >
              Back
            </Button>
            <div style={{ display: "flex", gap: "16px" }}>
              <Button
                htmlType="submit"
                type="primary"
                className="mint-btn"
                // onClick={() => setCurrent && setCurrent(1)}
              >
                Save changes
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Form>
  );
}
